/**
 * §51: админ-панель.
 *
 * Единственное место контракта, где наружу выходят имена провайдеров (§41)
 * и себестоимость в центах (§35). Всё остальное API этих полей не содержит.
 */
import { z } from 'zod';

/** Постраничная пагинация — только для админских таблиц (AGENTS.md §3.8). */
export const adminPageQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    query: z.string().trim().max(200).optional(),
    status: z.string().trim().max(40).optional(),
    kind: z.string().trim().max(40).optional(),
    provider: z.string().trim().max(60).optional(),
    operation: z.string().trim().max(60).optional(),
  })
  .strict();

export type AdminPageQuery = z.infer<typeof adminPageQuerySchema>;

export interface AdminPage<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/** Ручная операция с единицами. Причина обязательна — она видна пользователю. */
export const adminAdjustSchema = z
  .object({
    /**
     * Положительное — начисление, отрицательное — корректировка вниз.
     *
     * Потолок есть, потому что корректировка — единственная операция, которой
     * разрешено уводить баланс в минус (§7 экономики). Без границы лишний ноль
     * в поле даёт баланс вроде −999 899, и это не опечатка, которую видно:
     * запрос проходит успешно.
     */
    units: z
      .number()
      .int()
      .min(-1_000_000)
      .max(1_000_000)
      .refine((value) => value !== 0, 'Укажите количество'),
    reason: z.string().trim().min(3, 'Причина обязательна').max(200),
    /** Защита от двойного клика: без ключа повтор формы удвоит начисление. */
    idempotencyKey: z.string().trim().min(8).max(120),
  })
  .strict();

export type AdminAdjustInput = z.infer<typeof adminAdjustSchema>;

export const adminPriceSchema = z
  .object({
    units: z.number().int().min(0).max(100_000),
    isActive: z.boolean().optional(),
    reason: z.string().trim().max(200).optional(),
  })
  .strict();

export type AdminPriceInput = z.infer<typeof adminPriceSchema>;

export const adminProviderSchema = z
  .object({
    isEnabled: z.boolean().optional(),
    priority: z.number().int().min(0).max(1000).optional(),
    /** Сброс circuit breaker после разбора инцидента. */
    resetCircuit: z.boolean().optional(),
  })
  .strict();

export type AdminProviderInput = z.infer<typeof adminProviderSchema>;

/**
 * Ключи провайдера (C7).
 *
 * Пустая строка — «убрать поле», а не «записать пустое»: иначе очистить ключ
 * через форму было бы нечем. Значения наружу не возвращаются никогда.
 */
export const adminCredentialsSchema = z
  .object({
    apiKey: z.string().trim().max(500).optional(),
    apiSecret: z.string().trim().max(500).optional(),
    webhookSecret: z.string().trim().max(500).optional(),
    token: z.string().trim().max(500).optional(),
    baseUrl: z.string().trim().max(300).optional(),
  })
  .strict();

export type AdminCredentialsInput = z.infer<typeof adminCredentialsSchema>;

export interface AdminProviderCheck {
  ok: boolean;
  /** Что показать администратору: «отвечает» или причина отказа. */
  message: string;
  healthStatus: string;
}

export const adminUserStatusSchema = z
  .object({
    status: z.enum(['active', 'blocked']),
    reason: z.string().trim().max(200).optional(),
  })
  .strict();

export const adminReferralActionSchema = z
  .object({
    action: z.enum(['approve', 'revoke']),
    reason: z.string().trim().max(200).optional(),
  })
  .strict();

// ─── Представления ───────────────────────────────────────────────────────────

export interface AdminUserRow {
  id: string;
  email: string;
  name: string | null;
  role: string;
  /** §41: служебному аккаунту видна техническая изнанка его роликов. */
  isService: boolean;
  status: string;
  emailVerified: boolean;
  available: number;
  reserved: number;
  hasPersona: boolean;
  createdAt: string;
}

export interface AdminUserDetail extends AdminUserRow {
  spentUnitsTotal: number;
  projectCount: number;
  paymentCount: number;
  referredByCode: string | null;
  referralCode: string | null;
  consents: Array<{ type: string; version: string; acceptedAt: string }>;
  persona: { status: string; hasAvatar: boolean; hasVoice: boolean } | null;
}

export interface AdminProjectRow {
  id: string;
  title: string;
  ownerEmail: string;
  status: string;
  statusLabel: string;
  sceneCount: number;
  problemScenes: number;
  estimatedUnits: number;
  spentUnits: number;
  createdAt: string;
}

export interface AdminJobRow {
  id: string;
  userEmail: string;
  operation: string;
  status: string;
  progress: number;
  unitsReserved: number;
  unitsSpent: number;
  errorCode: string | null;
  /** Имя провайдера последней попытки — только здесь (§41). */
  provider: string | null;
  attempts: number;
  /** Себестоимость в центах — только здесь (§35). */
  costCents: number;
  createdAt: string;
  completedAt: string | null;
}

export interface AdminProviderJobView {
  id: string;
  provider: string;
  providerKind: string;
  /** Модель, которая отработала. Пусто у записей до появления отметки (§51). */
  model: string | null;
  status: string;
  attempt: number;
  errorCode: string | null;
  costCents: number;
  request: unknown;
  response: unknown;
  createdAt: string;
  completedAt: string | null;
}

export interface AdminJobDetail extends AdminJobRow {
  projectId: string | null;
  sceneId: string | null;
  input: unknown;
  result: unknown;
  providerJobs: AdminProviderJobView[];
}

export interface AdminProviderRow {
  id: string;
  name: string;
  kind: string;
  isEnabled: boolean;
  priority: number;
  healthStatus: string;
  failureCount: number;
  circuitOpenUntil: string | null;
  /** Есть ли адаптер в этом процессе: запись в БД без адаптера бесполезна. */
  loaded: boolean;
  jobsLastDay: number;
  failuresLastDay: number;
  avgSeconds: number | null;
  costCentsLastDay: number;
  /**
   * Заданные ключи в маскированном виде: `{ apiKey: 'sk-••••cdef' }`.
   * Настоящие значения не отдаются даже администратору (C7).
   */
  credentials: Record<string, string>;
  /** Требует ли провайдер ключей вообще: фейки и локальный движок — нет. */
  needsCredentials: boolean;
  /** Настроен ли: если нет, роутер его не выберет. */
  configured: boolean;
}

export interface AdminPriceRow {
  code: string;
  title: string;
  unitBasis: string;
  units: number;
  isActive: boolean;
  updatedAt: string;
}

export interface AdminReferralRow {
  id: string;
  referrerEmail: string;
  referredEmail: string;
  code: string;
  status: string;
  referrerReward: number;
  referredReward: number;
  note: string | null;
  createdAt: string;
}

export interface AdminAuditRow {
  id: string;
  actorEmail: string | null;
  action: string;
  entity: string;
  entityId: string | null;
  /**
   * Адрес запроса.
   *
   * На общем аккаунте почта одна на всех, и отличить людей можно только
   * по адресу и браузеру. Пусто — значит действие сделал не человек.
   */
  ip: string | null;
  meta: unknown;
  createdAt: string;
}

/**
 * Заведение аккаунта админом (§51).
 *
 * Нужно, пока приём оплаты не подключён: иначе тестировщику неоткуда взять
 * ни аккаунт, ни единицы.
 */
export const adminCreateUserSchema = z
  .object({
    email: z.string().trim().toLowerCase().email().max(200),
    name: z.string().trim().max(100).optional(),
    locale: z.string().trim().length(2).optional(),
    units: z.number().int().min(0).max(1_000_000).optional(),
    note: z.string().trim().max(200).optional(),
  })
  .strict();

export type AdminCreateUserInput = z.infer<typeof adminCreateUserSchema>;

export interface AdminCreatedUser {
  id: string;
  email: string;
  /** Показывается один раз: хранить его негде, а передать человеку надо. */
  password: string;
}

/**
 * Чем и на сколько работали (§51).
 *
 * Провайдер — компания, модель — то, что реально сделало кадр. Меняются
 * порознь, поэтому разрез по обоим сразу.
 */
export interface AdminUsageRow {
  provider: string;
  /** Пусто у записей, сделанных до появления этой отметки. */
  model: string | null;
  kind: string;
  calls: number;
  failed: number;
  /** §35: себестоимость не покидает админку. */
  costCents: number;
  lastUsedAt: string | null;
}

/** §41: отметка служебного аккаунта. */
export const adminServiceFlagSchema = z.object({ isService: z.boolean() }).strict();
